import React, { useCallback, useMemo } from 'react'

import { MenuItem, TableCell, TableRow, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'
import PropTypes from 'prop-types'

import SectionTable from './components/SectionTable'
import { feeWaiverType } from './constants'

const ALL_OPTIONS = ['ALL', 'US', 'NONE']
// may need to be applied upwards
const DEFAULT_FEE_WAIVER = {
  reducedLunch: 'US', // LOCKED
  collegeBoardACTOrNACAC: 'US', // can also be ALL
  pellGrant: 'US', // LOCKED
  trio: 'US', // LOCKED
  armedForces: '', // any
  firstGeneration: '', // any
  publicAssistance: '', // any
  federalStateLocalAssistance: '', // any
  subsidizedHousing: '', // any
  needBasedScholarship: '', // any
  orphanOrWard: '', // any
  collegeAssistance: '', // any
}
export const FEE_WAIVER_KEYS = Object.keys(DEFAULT_FEE_WAIVER)
export const FEE_WAIVERS = [
  {
    key: 'reducedLunch',
    values: ['US'],
    locked: true,
    label: 'Students who qualify for Federal Free and Reduced Lunch Program',
  },
  {
    key: 'collegeBoardACTOrNACAC',
    values: ['US', 'ALL'],
    locked: false,
    label: 'Students who have received a College Board, ACT, and/or NACAC fee waiver',
  },
  { key: 'trio', values: ['US'], locked: true, label: 'Students who are participating in TRiO programs' },
  { key: 'pellGrant', values: ['US'], locked: true, label: 'Students who receive a Pell Grant' },
  {
    key: 'armedForces',
    values: ALL_OPTIONS,
    locked: false,
    label: 'Students who are veterans or active members of the U.S. Armed Forces',
  },
  {
    key: 'firstGeneration',
    values: ALL_OPTIONS,
    locked: false,
    label: 'Students who self-identify as the first in their family to attend college',
  },
  {
    key: 'publicAssistance',
    values: ALL_OPTIONS,
    locked: false,
    label: 'Students whose family receives public assistance',
  },
  {
    key: 'federalStateLocalAssistance',
    values: ALL_OPTIONS,
    locked: false,
    label: 'Students enrolled in a federal, state, or local program that aids students from low-income families',
  },
  {
    key: 'subsidizedHousing',
    values: ALL_OPTIONS,
    locked: false,
    label: 'Students who live in federally subsidized public housing, a foster home or are homeless',
  },
  {
    key: 'orphanOrWard',
    values: ALL_OPTIONS,
    locked: false,
    label: 'Students who are a ward of the state or an orphan',
  },
  {
    key: 'needBasedScholarship',
    values: ALL_OPTIONS,
    locked: false,
    label: 'Students who receive need-based scholarships elsewhere',
  },
  {
    key: 'collegeAssistance',
    values: ALL_OPTIONS,
    locked: false,
    label: 'Students who are being assisted by any college or university access program',
  },
]

const useStyles = makeStyles({
  sectionRow: {
    height: 55,
  },
})
const FeeWaiversSection = ({
  isEditing,
  applicationConfig,
  updateApplicationConfig,
  loading,
  getStyle,
  showWarnings,
}) => {
  const classes = useStyles()
  const relevantConfig = useMemo(() => applicationConfig.feeWaivers ?? { ...DEFAULT_FEE_WAIVER }, [applicationConfig])
  const onUpdate = useCallback(
    (key, value) => {
      updateApplicationConfig({
        feeWaivers: {
          ...relevantConfig,
          [key]: value,
        },
      })
    },
    [relevantConfig, updateApplicationConfig],
  )
  return (
    <SectionTable
      sectionName="Fee Waivers"
      showFirstYearHeader={false}
      showTransferHeader={false}
      getStyle={getStyle}
    >
      {FEE_WAIVERS.map(({ key, values, locked, label }) => (
        <TableRow
          key={key}
          className={classes.sectionRow}
        >
          <TableCell
            align="left"
            variant="body"
            component="th"
            size="small"
            style={{ color: getStyle() }}
          >
            {label}
          </TableCell>
          <TableCell aria-hidden={true}></TableCell>
          <TableCell
            align="left"
            size="small"
          >
            {loading ? (
              <Skeleton
                width={115}
                height={18}
              />
            ) : isEditing ? (
              <TextField
                select
                required
                error={showWarnings && !relevantConfig[key]}
                margin="dense"
                small="small"
                disabled={locked || !isEditing}
                label="Select"
                id={key}
                value={relevantConfig[key]}
                onChange={(e) => {
                  onUpdate(key, e.target.value)
                }}
                variant="outlined"
                fullWidth
              >
                {values.map((value) => (
                  <MenuItem
                    key={value}
                    value={value}
                  >
                    {/* margin="normal" */}
                    {feeWaiverType[value]}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              feeWaiverType[relevantConfig[key]] ?? 'Not Selected'
            )}
          </TableCell>
        </TableRow>
      ))}
    </SectionTable>
  )
}

FeeWaiversSection.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  applicationConfig: PropTypes.object.isRequired,
  updateApplicationConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  getStyle: PropTypes.func.isRequired,
  showWarnings: PropTypes.bool,
}

export default FeeWaiversSection
